import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import AdminData from "./AdminData";
import AdminBlogReview from "./AdminBlogReview";
import AdminBlogPost from "./AdminBlogPost";

const TABS = [
  { id: 'stats', label: '📊 Stats',       path: '/stats' },
  { id: 'blog',  label: '📝 Blog Review', path: '/blog'  },
];

const AdminDashboard = ({ user }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const activeTab = location.pathname.startsWith('/blog') ? 'blog' : 'stats';

  useEffect(() => {
    if (activeTab === 'stats') fetchAdminData();
  }, [activeTab]);

  const fetchAdminData = async () => {
    setLoading(true);
    setError('');
    try {
      const adminData = httpsCallable(getFunctions(), 'adminData');
      const result = await adminData();
      setData(result.data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome, {user.email}</p>
          </div>
          <div className="flex gap-3">
            {activeTab === 'stats' && (
              <button
                onClick={fetchAdminData}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            )}
            <button
              onClick={() => getAuth().signOut()}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-5 border-b border-gray-200">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`px-5 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Routed content */}
      <Routes>
        <Route path="/" element={<Navigate to="/stats" replace />} />
        <Route path="/stats" element={
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 z-50 flex flex-col items-center justify-start pt-6 bg-white/60 backdrop-blur-sm rounded-lg">
                <div className="text-center px-6">
                  <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-gray-700 text-base font-semibold">Loading admin data…</p>
                </div>
              </div>
            )}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                <strong>Error:</strong> {error}
              </div>
            )}
            {data && <AdminData data={data} />}
          </div>
        } />
        <Route path="/blog" element={<AdminBlogReview />} />
        <Route path="/blog/:docId" element={<AdminBlogPost />} />
        <Route path="*" element={<Navigate to="/stats" replace />} />
      </Routes>
    </div>
  );
};

export default AdminDashboard;
